import {
  FieldSet,
  FormattedString,
  FormContext,
  FormState,
  InputWizardPanelComponent,
  IpInput,
  NetworkAddressInput,
  SelectComponent,
  TextInput,
  uniqueGUID,
} from "o4a-react";
import * as React from "react";
import * as Messages from "../../codegen/Messages";
import { McvcnSelect } from "../../components/McvcnSelect/McvcnSelect";
import { VNETSelect } from "../../components/VNETSelect/VNETSelect";
import { networkCidrSizingUrl, vnetTroubleshooting } from "../../constants/docConstants";
import { AzureVirtualNetworkSummary } from "../../gen/clients/mchub-azure-api-client";
import { IdResourceType } from "../../helpers/idHelper";
import { FutureMcvcnExisting, FutureMcvcnNew, maxNetworkLinkCidrPrefix } from "../../helpers/resourceHelper";
import {
  alphaNumericHyphenRegex,
  startWithLetterRegex,
  validateNetworkLinkAddressSpace,
  vmClusterHostnameMaxLength,
} from "../../helpers/validationHelper";
import { useFeatures } from "../../hooks/useFeatures";
import { useOperation } from "../../hooks/useOperation";
import { McvcnCreateNewProps, newMcvcnCreateNew } from "../../operations/Mcvcn/McvcnCreateNew";

export const GROUP_NETWORK = "network";

export enum Fields {
  Cidrs= "networkLinkDetails.ociNetworkCidrs",
  Vnet= "networkLinkDetails.azureAttachedNetworkIds",
  Hostname= "cloudVmClusterDetails.hostname",
  Mcvcn= "multiCloudVirtualNetworkDetails.id",
  NetworkVirtualAppliance= "networkLinkDetails.customerNvaIpAddress",
}

export enum FieldTestIds {
  Cidrs = "ociNetworkCidrs",
  Vnet = "azureAttachedNetworkIds",
  Hostname = "hostname",
  Mcvcn = "id",
  NetworkVirtualAppliance = "customerNvaIpAddress",
}

export enum LinkTestIds {
  McvcnCreateNewLink = "mcvcnCreateNewLink",
}

export interface NetworkTabContentProps {
  inputWizardPanelRef: InputWizardPanelComponent;
  subscriptionId: string;
  location: string;
}

export const NetworkTabContent = ({
  inputWizardPanelRef,
  subscriptionId,
  location,
}: NetworkTabContentProps): JSX.Element => {
  const { enableMcvcn, enableExaMcvcnCreate } = useFeatures();
  const [selectedVnet, setSelectedVnet] = React.useState<AzureVirtualNetworkSummary | undefined>(undefined);
  const [selectRef, setSelectRef] = React.useState<SelectComponent>();
  const [renderKey, setRenderKey] = React.useState<string>(uniqueGUID());
  const form: FormState = React.useContext(FormContext);

  const { trigger: triggerMcvcnCreateNew } = useOperation<McvcnCreateNewProps>(newMcvcnCreateNew);

  const onError = (): void => inputWizardPanelRef.showError(Messages.createCommon.loadingErrors.general());
  const onMissingDependencies = (): void => {
    inputWizardPanelRef.showErrorDialog(
      Messages.createCommon.dependencyWarnings.basicsIncomplete.message(),
      Messages.createCommon.dependencyWarnings.basicsIncomplete.title(),
    );
  };

  const onChangeVnet = (_: string, vnet: AzureVirtualNetworkSummary | undefined): void => {
    form.setValue(undefined, Fields.Cidrs, GROUP_NETWORK);
    setSelectedVnet(vnet);
    setRenderKey(uniqueGUID());
  };

  const onLinkClick = (): void => {
    const missingDependencies: IdResourceType[] = [];
    if (!subscriptionId) missingDependencies.push(IdResourceType.SUBSCRIPTIONS);
    else if (!location) missingDependencies.push(IdResourceType.LOCATION);

    if (missingDependencies.length > 0) {
      onMissingDependencies?.();
      return; // Do not display McvcnCreate if dependencies missing
    }

    triggerMcvcnCreateNew({
      location,
      subscriptionId,
      onExecute: (futureMcvcn: FutureMcvcnExisting | FutureMcvcnNew): void => {
        selectRef?.setOptionNew(futureMcvcn.name, futureMcvcn);
      },
    });
  };

  const createVcnLink = {
    id: "create_new_vcn",
    testId: LinkTestIds.McvcnCreateNewLink,
    text: Messages.actions.createNew(),
    onLinkClick,
  };

  return (
    <>
      <FieldSet header={Messages.createExaDb.networkingTab.sectionTitles.vmClusterNetworking()}>
        <TextInput
          required
          testId={FieldTestIds.Hostname}
          fieldName={Fields.Hostname}
          label={Messages.labels.hostName()}
          validator={(value: string | undefined) => {
            const errors: string[] = [];
            if (!value || !startWithLetterRegex.test(value)) {
              errors.push(Messages.validation.nameStartChar());
            }
            if (!value || !alphaNumericHyphenRegex.test(value)) {
              errors.push(Messages.validation.valueAlphaNumericHyphen());
            }
            if (!value || value.length > vmClusterHostnameMaxLength) {
              errors.push(Messages.validation.valueMaxLen(vmClusterHostnameMaxLength.toString()));
            }
            return errors.length ? errors : undefined;
          }}
        />
      </FieldSet>
      <FieldSet header={Messages.createExaDb.networkingTab.sectionTitles.networkPeering()}>
        {!(enableMcvcn && enableExaMcvcnCreate) && (
          <>
            <VNETSelect
              required
              testId={FieldTestIds.Vnet}
              fieldName={Fields.Vnet}
              label={Messages.labels.virtualNetwork()}
              tooltip={
                FormattedString(
                  { inputText: Messages.hints.tooltipVNET(vnetTroubleshooting) },
                ) as unknown as string
              }
              subscriptionId={subscriptionId}
              location={location}
              onChange={onChangeVnet}
              onError={onError}
              onMissingDependencies={onMissingDependencies}
            />
            <IpInput
              testId={FieldTestIds.NetworkVirtualAppliance}
              fieldName={Fields.NetworkVirtualAppliance}
              label={Messages.labels.networkVirtualAppliance()}
              tooltip={Messages.hints.tooltipNetworkVirtualAppliance()}
              validation={{
                containsIpAddress: true,
                containsCidrBlock: false,
              }}
            />
            <NetworkAddressInput
              required
              key={renderKey}
              testId={FieldTestIds.Cidrs}
              fieldName={Fields.Cidrs}
              groupName={GROUP_NETWORK}
              label={Messages.labels.ociCidr()}
              tooltip={FormattedString(
                { inputText: Messages.hints.toolTipCidr(networkCidrSizingUrl) },
              ) as unknown as string}
              validator={validateNetworkLinkAddressSpace(maxNetworkLinkCidrPrefix, selectedVnet?.addressSpaces)}
            />
          </>
        )}
        {enableMcvcn && enableExaMcvcnCreate && (
          <McvcnSelect
            required
            testId={FieldTestIds.Mcvcn}
            label={Messages.labels.virtualCloudNetwork()}
            componentRef={setSelectRef}
            location={location}
            fieldName={Fields.Mcvcn}
            groupName={GROUP_NETWORK}
            subscriptionId={subscriptionId}
            inputLink={createVcnLink}
            onError={onError}
            onMissingDependencies={onMissingDependencies}
          />
        )}
      </FieldSet>
    </>
  );
};
